import { ReactNode, useId } from 'react';
import clsx from 'clsx';

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: (id: string) => ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: FieldShellProps): JSX.Element {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted">
        {label}
        {required && <span className="ml-0.5 text-danger" aria-hidden>*</span>}
      </label>
      {children(id)}
      {error ? (
        <p className="text-[11px] text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[11px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

const baseInputClasses = clsx(
  'h-9 w-full rounded-md border border-border bg-surface-2 px-3 text-sm text-foreground',
  'placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
  'disabled:cursor-not-allowed disabled:opacity-60',
);

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function TextInput({ invalid, className, ...rest }: TextInputProps): JSX.Element {
  return (
    <input
      {...rest}
      className={clsx(baseInputClasses, invalid && 'border-danger', className)}
    />
  );
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export function Select({ invalid, className, children, ...rest }: SelectProps): JSX.Element {
  return (
    <select
      {...rest}
      className={clsx(baseInputClasses, invalid && 'border-danger', className)}
    >
      {children}
    </select>
  );
}

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function TextArea({ invalid, className, ...rest }: TextAreaProps): JSX.Element {
  return (
    <textarea
      {...rest}
      className={clsx(
        'min-h-[72px] w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-foreground',
        'placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        'disabled:cursor-not-allowed disabled:opacity-60',
        invalid && 'border-danger',
        className,
      )}
    />
  );
}
